import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  Text,
  Title,
  Badge,
  Group,
  Stack,
  Progress,
  Alert,
  ActionIcon,
  Tooltip,
  Button,
  Switch,
  Center,
  Loader,
  Table,
  ScrollArea,
  RingProgress,
  ThemeIcon,
  Timeline,
  Indicator
} from '@mantine/core';
import {
  IconServer,
  IconDatabase,
  IconAlertTriangle,
  IconRefresh,
  IconWifi,
  IconWifiOff,
  IconClock,
  IconLock,
  IconLockOpen,
  IconHeartbeat,
  IconShield,
  IconActivity,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconExclamationMark
} from '@tabler/icons-react';
import { useQuery, useSubscription } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import { useCurrentTenant } from '../hooks/useCurrentTenant';
import { useAuditLogger } from '../hooks/useAuditLogger';

// GraphQL queries for coordination health
const GET_COORDINATION_HEALTH = gql`
  query GetCoordinationHealth($tenantId: String!) {
    sagaCoordinationHealth(tenantId: $tenantId) {
      redisCluster {
        status
        nodes {
          nodeId
          host
          port
          status
          role
          connectedClients
          memoryUsage
          lastPingMs
        }
        totalConnections
        averageLatencyMs
        failedOperations
        lastHealthCheck
      }
      distributedLocks {
        activeLocks
        averageLockDurationMs
        expiredLocks
        contentionCount
        lockTimeouts
        emergencyUnlocks
      }
      coordinatorInstances {
        instanceId
        status
        lastHeartbeat
        activeSagas
        coordinatingTenants
        memoryUsageBytes
        cpuUsagePercent
        uptime
        version
      }
      systemMetrics {
        overallHealth
        availabilityPercent
        responseTimeP99Ms
        errorRate
        activeConnections
        throughputOpsPerSecond
      }
    }
  }
`;

const SUBSCRIBE_COORDINATION_ALERTS = gql`
  subscription SubscribeCoordinationAlerts($tenantId: String!, $primitiveTypes: [String!]) {
    sagaStateTransitions(tenant: $tenantId, primitiveTypes: $primitiveTypes) {
      eventId
      tenant
      sagaExecutionId
      eventType
      primitiveType
      fromState
      toState
      stepId
      executionResult
      auditTrailId
      timestamp
      userId
      safetyScore
      performanceMetrics
    }
  }
`;

interface RedisNode {
  nodeId: string;
  host: string;
  port: number;
  status: 'HEALTHY' | 'WARNING' | 'CRITICAL' | 'OFFLINE';
  role: 'MASTER' | 'REPLICA' | 'SENTINEL';
  connectedClients: number;
  memoryUsage: number;
  lastPingMs: number;
}

interface RedisCluster {
  status: 'HEALTHY' | 'WARNING' | 'CRITICAL' | 'OFFLINE';
  nodes: RedisNode[];
  totalConnections: number;
  averageLatencyMs: number;
  failedOperations: number;
  lastHealthCheck: string;
}

interface DistributedLocks {
  activeLocks: number;
  averageLockDurationMs: number;
  expiredLocks: number;
  contentionCount: number;
  lockTimeouts: number;
  emergencyUnlocks: number;
}

interface CoordinatorInstance {
  instanceId: string;
  status: 'HEALTHY' | 'WARNING' | 'CRITICAL' | 'OFFLINE';
  lastHeartbeat: string;
  activeSagas: number;
  coordinatingTenants: number;
  memoryUsageBytes: number;
  cpuUsagePercent: number;
  uptime: number;
  version: string;
}

interface SystemMetrics {
  overallHealth: number;
  availabilityPercent: number;
  responseTimeP99Ms: number;
  errorRate: number;
  activeConnections: number;
  throughputOpsPerSecond: number;
}

interface CoordinationHealth {
  redisCluster: RedisCluster;
  distributedLocks: DistributedLocks;
  coordinatorInstances: CoordinatorInstance[];
  systemMetrics: SystemMetrics;
}

interface CoordinationAlert {
  alertType: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  message: string;
  component: 'REDIS' | 'COORDINATOR' | 'LOCKS' | 'SYSTEM';
  timestamp: string;
  metadata: Record<string, any>;
  resolution?: string;
}

export const SagaCoordinationHealthMonitor: React.FC = () => {
  const { currentTenant } = useCurrentTenant();
  const { logUserAction } = useAuditLogger();

  // State management
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [alerts, setAlerts] = useState<CoordinationAlert[]>([]);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(null);

  // GraphQL queries
  const { data: healthData, loading, error, refetch } = useQuery<Record<string, any>>(GET_COORDINATION_HEALTH, {
    variables: { tenantId: currentTenant?.tenantId },
    pollInterval: autoRefresh ? 10000 : 0, // 10 second polling for coordination health
    skip: !currentTenant?.tenantId
  });

  // Real-time alert subscription (mapped from state transitions)
  useSubscription<Record<string, any>>(SUBSCRIBE_COORDINATION_ALERTS, {
    variables: {
      tenantId: currentTenant?.tenantId,
      primitiveTypes: undefined // Listen to all primitive types
    },
    skip: !currentTenant?.tenantId,
    onData: ({ data }) => {
      if (data.data?.sagaStateTransitions) {
        const event = data.data?.sagaStateTransitions;

        // Convert state transition to coordination alert (filter for coordination issues)
        if (event.toState === 'failed' || event.toState === 'error' || event.toState === 'timeout' || event.toState === 'deadlock') {
          const alert: CoordinationAlert = {
            alertType: event.toState === 'timeout' ? 'LOCK_TIMEOUT' : event.toState === 'deadlock' ? 'DEADLOCK_DETECTED' : 'COORDINATION_FAILURE',
            severity: event.toState === 'deadlock' ? 'CRITICAL' : event.toState === 'timeout' ? 'HIGH' : event.toState === 'failed' ? 'HIGH' : 'MEDIUM',
            message: `${event.eventType} coordination issue: ${event.toState}`,
            component: event.primitiveType === 'wfl' ? 'COORDINATOR' : event.primitiveType === 'ins' ? 'REDIS' : 'SYSTEM',
            timestamp: event.timestamp,
            metadata: { eventId: event.eventId, fromState: event.fromState, toState: event.toState, sagaExecutionId: event.sagaExecutionId },
            resolution: undefined
          };

          setAlerts(prev => [alert, ...prev.slice(0, 99)]); // Keep last 100 alerts

          // Show notification for critical coordination alerts
          if (alert.severity === 'CRITICAL') {
            notifications.show({
              title: `${alert.component} Critical Alert`,
              message: alert.message,
              color: 'red',
              icon: <IconAlertTriangle size={20} />,
              autoClose: false
            });
          }

          // Log alert for audit trail
          logUserAction({
            action: 'saga_coordination_alert_received',
            details: {
              component: alert.component,
              alertType: alert.alertType,
              severity: alert.severity
            },
            metadata: alert.metadata
          });
        }
      }
    }
  });

  const health = healthData?.sagaCoordinationHealth as CoordinationHealth | undefined;

  // Calculate component statuses
  const componentStatuses = useMemo(() => {
    if (!health) return {};

    return {
      redis: {
        status: health.redisCluster.status,
        score: health.redisCluster.nodes.filter(n => n.status === 'HEALTHY').length / health.redisCluster.nodes.length * 100,
        details: `${health.redisCluster.nodes.filter(n => n.status === 'HEALTHY').length}/${health.redisCluster.nodes.length} nodes healthy`
      },
      coordinators: {
        status: health.coordinatorInstances.every(c => c.status === 'HEALTHY') ? 'HEALTHY' :
                health.coordinatorInstances.some(c => c.status === 'CRITICAL') ? 'CRITICAL' : 'WARNING',
        score: health.coordinatorInstances.filter(c => c.status === 'HEALTHY').length / health.coordinatorInstances.length * 100,
        details: `${health.coordinatorInstances.filter(c => c.status === 'HEALTHY').length}/${health.coordinatorInstances.length} instances healthy`
      },
      locks: {
        status: health.distributedLocks.lockTimeouts > 5 ? 'CRITICAL' :
                health.distributedLocks.emergencyUnlocks > 0 ? 'WARNING' : 'HEALTHY',
        score: Math.max(0, 100 - (health.distributedLocks.lockTimeouts + health.distributedLocks.emergencyUnlocks) * 2),
        details: `${health.distributedLocks.activeLocks} active locks, ${health.distributedLocks.lockTimeouts} timeouts`
      },
      system: {
        status: health.systemMetrics.overallHealth >= 95 ? 'HEALTHY' :
                health.systemMetrics.overallHealth >= 85 ? 'WARNING' : 'CRITICAL',
        score: health.systemMetrics.overallHealth,
        details: `${health.systemMetrics.availabilityPercent.toFixed(2)}% availability`
      }
    };
  }, [health]);

  // Handle manual refresh
  const handleRefresh = useCallback(async () => {
    try {
      await refetch();
      logUserAction({
        action: 'saga_coordination_health_refresh'
      });
      notifications.show({
        title: 'Health Data Refreshed',
        message: 'Coordination health metrics updated successfully',
        color: 'green'
      });
    } catch (error) {
      notifications.show({
        title: 'Refresh Failed',
        message: 'Failed to refresh coordination health data',
        color: 'red'
      });
    }
  }, [refetch, logUserAction]);

  // Handle emergency intervention
  const handleEmergencyIntervention = useCallback((component: string, action: string) => {
    logUserAction({
      action: 'saga_coordination_emergency_intervention',
      details: {
        component,
        interventionAction: action,
        reason: 'manual_operator_intervention'
      }
    });

    notifications.show({
      title: 'Emergency Intervention Logged',
      message: `${action} for ${component} has been logged for immediate attention`,
      color: 'orange',
      icon: <IconAlertTriangle size={20} />
    });
  }, [logUserAction]);

  if (loading) {
    return (
      <Center h={400}>
        <Stack ta="center">
          <Loader size="lg" />
          <Text>Loading coordination health data...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        title="Error Loading Coordination Health"
        color="red"
        variant="outline"
      >
        Failed to load coordination health data: {error.message}
      </Alert>
    );
  }

  if (!health) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        title="No Health Data Available"
        color="yellow"
        variant="outline"
      >
        No coordination health data available for the current tenant.
      </Alert>
    );
  }

  return (
    <Container size="xl" p="md">
      <Stack gap="lg">
        {/* Header Section */}
        <Group justify="space-between" ta="center">
          <div>
            <Title order={2}>SAGA Coordination Health Monitor</Title>
            <Text size="sm" c="dimmed">
              Real-time monitoring of distributed coordination infrastructure
            </Text>
          </div>

          <Group gap="sm">
            <Switch
              label="Auto Refresh"
              checked={autoRefresh}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAutoRefresh(event.currentTarget.checked)}
              size="sm"
            />

            <Tooltip label="Refresh health data">
              <ActionIcon onClick={handleRefresh} variant="outline">
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {/* System Overview */}
        <Card withBorder p="lg">
          <Group justify="space-between" ta="center" mb="md">
            <Title order={3}>System Overview</Title>
            <Badge
              color={
                health.systemMetrics.overallHealth >= 95 ? 'green' :
                health.systemMetrics.overallHealth >= 85 ? 'orange' : 'red'
              }
              size="lg"
              variant="filled"
            >
              {health.systemMetrics.overallHealth.toFixed(1)}% Health
            </Badge>
          </Group>

          <Grid>
            <Grid.Col span={3}>
              <Center>
                <RingProgress
                  size={120}
                  thickness={12}
                  sections={[
                    {
                      value: health.systemMetrics.availabilityPercent,
                      color: health.systemMetrics.availabilityPercent >= 99.9 ? 'green' :
                             health.systemMetrics.availabilityPercent >= 99.5 ? 'orange' : 'red'
                    }
                  ]}
                  label={
                    <Text size="xs" ta="center">
                      <Text size="lg" fw={700}>
                        {health.systemMetrics.availabilityPercent.toFixed(2)}%
                      </Text>
                      Availability
                    </Text>
                  }
                />
              </Center>
            </Grid.Col>

            <Grid.Col span={9}>
              <Grid>
                <Grid.Col span={4}>
                  <Stack gap="xs">
                    <Text size="sm" c="dimmed">Response Time (P99)</Text>
                    <Text size="lg" fw={600}>{health.systemMetrics.responseTimeP99Ms}ms</Text>
                    <Progress
                      value={Math.max(0, 100 - (health.systemMetrics.responseTimeP99Ms / 10))}
                      size="sm"
                      color={health.systemMetrics.responseTimeP99Ms <= 100 ? 'green' :
                             health.systemMetrics.responseTimeP99Ms <= 500 ? 'orange' : 'red'}
                    />
                  </Stack>
                </Grid.Col>

                <Grid.Col span={4}>
                  <Stack gap="xs">
                    <Text size="sm" c="dimmed">Error Rate</Text>
                    <Text size="lg" fw={600} c="red">{health.systemMetrics.errorRate.toFixed(3)}%</Text>
                    <Progress
                      value={Math.max(0, 100 - (health.systemMetrics.errorRate * 100))}
                      size="sm"
                      color={health.systemMetrics.errorRate <= 0.01 ? 'green' :
                             health.systemMetrics.errorRate <= 0.1 ? 'orange' : 'red'}
                    />
                  </Stack>
                </Grid.Col>

                <Grid.Col span={4}>
                  <Stack gap="xs">
                    <Text size="sm" c="dimmed">Throughput</Text>
                    <Text size="lg" fw={600}>{health.systemMetrics.throughputOpsPerSecond.toLocaleString()} ops/s</Text>
                    <Text size="sm" c="dimmed">{health.systemMetrics.activeConnections} active connections</Text>
                  </Stack>
                </Grid.Col>
              </Grid>
            </Grid.Col>
          </Grid>
        </Card>

        {/* Component Health Grid */}
        <Title order={3}>Component Health Status</Title>
        <Grid>
          {/* Redis Cluster */}
          <Grid.Col span={6}>
            <Card
              withBorder
              p="md"
              style={{
                cursor: 'pointer',
                borderColor: selectedComponent === 'redis' ? 'var(--mantine-color-blue-4)' : undefined
              }}
              onClick={() => setSelectedComponent(selectedComponent === 'redis' ? null : 'redis')}
            >
              <Stack gap="md">
                <Group justify="space-between">
                  <Group gap="sm">
                    <ThemeIcon
                      color={health.redisCluster.status === 'HEALTHY' ? 'green' :
                             health.redisCluster.status === 'WARNING' ? 'orange' : 'red'}
                      variant="light"
                    >
                      <IconDatabase size={20} />
                    </ThemeIcon>
                    <div>
                      <Text fw={600}>Redis Cluster</Text>
                      <Text size="sm" c="dimmed">{componentStatuses.redis?.details}</Text>
                    </div>
                  </Group>
                  <Badge color={health.redisCluster.status === 'HEALTHY' ? 'green' :
                               health.redisCluster.status === 'WARNING' ? 'orange' : 'red'}>
                    {health.redisCluster.status}
                  </Badge>
                </Group>

                <Group justify="space-between">
                  <Text size="sm">Avg Latency: {health.redisCluster.averageLatencyMs}ms</Text>
                  <Text size="sm">Connections: {health.redisCluster.totalConnections}</Text>
                </Group>

                {health.redisCluster.failedOperations > 0 && (
                  <Alert color="red" icon={<IconAlertTriangle size={14} />}>
                    {health.redisCluster.failedOperations} failed operations
                  </Alert>
                )}
              </Stack>
            </Card>
          </Grid.Col>

          {/* Coordinator Instances */}
          <Grid.Col span={6}>
            <Card
              withBorder
              p="md"
              style={{
                cursor: 'pointer',
                borderColor: selectedComponent === 'coordinators' ? 'var(--mantine-color-blue-4)' : undefined
              }}
              onClick={() => setSelectedComponent(selectedComponent === 'coordinators' ? null : 'coordinators')}
            >
              <Stack gap="md">
                <Group justify="space-between">
                  <Group gap="sm">
                    <ThemeIcon
                      color={componentStatuses.coordinators?.status === 'HEALTHY' ? 'green' :
                             componentStatuses.coordinators?.status === 'WARNING' ? 'orange' : 'red'}
                      variant="light"
                    >
                      <IconServer size={20} />
                    </ThemeIcon>
                    <div>
                      <Text fw={600}>Coordinator Instances</Text>
                      <Text size="sm" c="dimmed">{componentStatuses.coordinators?.details}</Text>
                    </div>
                  </Group>
                  <Badge color={componentStatuses.coordinators?.status === 'HEALTHY' ? 'green' :
                               componentStatuses.coordinators?.status === 'WARNING' ? 'orange' : 'red'}>
                    {componentStatuses.coordinators?.status}
                  </Badge>
                </Group>

                <Group justify="space-between">
                  <Text size="sm">
                    Active SAGAs: {health.coordinatorInstances.reduce((sum, c) => sum + c.activeSagas, 0)}
                  </Text>
                  <Text size="sm">
                    Tenants: {health.coordinatorInstances.reduce((sum, c) => sum + c.coordinatingTenants, 0)}
                  </Text>
                </Group>
              </Stack>
            </Card>
          </Grid.Col>

          {/* Distributed Locks */}
          <Grid.Col span={6}>
            <Card
              withBorder
              p="md"
              style={{
                cursor: 'pointer',
                borderColor: selectedComponent === 'locks' ? 'var(--mantine-color-blue-4)' : undefined
              }}
              onClick={() => setSelectedComponent(selectedComponent === 'locks' ? null : 'locks')}
            >
              <Stack gap="md">
                <Group justify="space-between">
                  <Group gap="sm">
                    <ThemeIcon
                      color={componentStatuses.locks?.status === 'HEALTHY' ? 'green' :
                             componentStatuses.locks?.status === 'WARNING' ? 'orange' : 'red'}
                      variant="light"
                    >
                      <IconLock size={20} />
                    </ThemeIcon>
                    <div>
                      <Text fw={600}>Distributed Locks</Text>
                      <Text size="sm" c="dimmed">{componentStatuses.locks?.details}</Text>
                    </div>
                  </Group>
                  <Badge color={componentStatuses.locks?.status === 'HEALTHY' ? 'green' :
                               componentStatuses.locks?.status === 'WARNING' ? 'orange' : 'red'}>
                    {componentStatuses.locks?.status}
                  </Badge>
                </Group>

                <Group justify="space-between">
                  <Text size="sm">Avg Duration: {health.distributedLocks.averageLockDurationMs}ms</Text>
                  <Text size="sm">Contention: {health.distributedLocks.contentionCount}</Text>
                </Group>

                {(health.distributedLocks.lockTimeouts > 0 || health.distributedLocks.emergencyUnlocks > 0) && (
                  <Alert color="orange" icon={<IconAlertTriangle size={14} />}>
                    {health.distributedLocks.lockTimeouts} timeouts, {health.distributedLocks.emergencyUnlocks} emergency unlocks
                  </Alert>
                )}
              </Stack>
            </Card>
          </Grid.Col>

          {/* System Performance */}
          <Grid.Col span={6}>
            <Card withBorder p="md">
              <Stack gap="md">
                <Group justify="space-between">
                  <Group gap="sm">
                    <ThemeIcon
                      color={componentStatuses.system?.status === 'HEALTHY' ? 'green' :
                             componentStatuses.system?.status === 'WARNING' ? 'orange' : 'red'}
                      variant="light"
                    >
                      <IconActivity size={20} />
                    </ThemeIcon>
                    <div>
                      <Text fw={600}>System Performance</Text>
                      <Text size="sm" c="dimmed">{componentStatuses.system?.details}</Text>
                    </div>
                  </Group>
                  <Badge color={componentStatuses.system?.status === 'HEALTHY' ? 'green' :
                               componentStatuses.system?.status === 'WARNING' ? 'orange' : 'red'}>
                    {componentStatuses.system?.status}
                  </Badge>
                </Group>

                <Progress
                  value={health.systemMetrics.overallHealth}
                  size="lg"
                  color={health.systemMetrics.overallHealth >= 95 ? 'green' :
                         health.systemMetrics.overallHealth >= 85 ? 'orange' : 'red'}
                />
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Detailed Component View */}
        {selectedComponent && (
          <Card withBorder p="lg">
            <Stack gap="md">
              <Group justify="space-between">
                <Title order={4}>
                  {selectedComponent === 'redis' ? 'Redis Cluster Details' :
                   selectedComponent === 'coordinators' ? 'Coordinator Instance Details' :
                   selectedComponent === 'locks' ? 'Distributed Lock Details' : 'Component Details'}
                </Title>
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() => setSelectedComponent(null)}
                >
                  Close Details
                </Button>
              </Group>

              {selectedComponent === 'redis' && (
                <ScrollArea h={300}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Node</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Role</Table.Th>
                        <Table.Th>Clients</Table.Th>
                        <Table.Th>Memory</Table.Th>
                        <Table.Th>Ping</Table.Th>
                        <Table.Th>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {health.redisCluster.nodes.map((node) => (
                        <Table.Tr key={node.nodeId}>
                          <Table.Td>
                            <Text size="sm" ff="monospace">{node.host}:{node.port}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Group gap="xs">
                              {node.status === 'HEALTHY' ? <IconCheck size={14} color="green" /> :
                               node.status === 'WARNING' ? <IconExclamationMark size={14} color="orange" /> :
                               <IconX size={14} color="red" />}
                              <Text size="sm">{node.status}</Text>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Badge size="sm" color={node.role === 'MASTER' ? 'blue' : 'gray'}>
                              {node.role}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{node.connectedClients}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{(node.memoryUsage / 1024 / 1024).toFixed(1)}MB</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{node.lastPingMs}ms</Text>
                          </Table.Td>
                          <Table.Td>
                            {node.status !== 'HEALTHY' && (
                              <Button
                                size="xs"
                                color="orange"
                                onClick={() => handleEmergencyIntervention('redis', `restart_node_${node.nodeId}`)}
                              >
                                Restart
                              </Button>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}

              {selectedComponent === 'coordinators' && (
                <ScrollArea h={300}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Instance</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Heartbeat</Table.Th>
                        <Table.Th>SAGAs</Table.Th>
                        <Table.Th>Memory</Table.Th>
                        <Table.Th>CPU</Table.Th>
                        <Table.Th>Uptime</Table.Th>
                        <Table.Th>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {health.coordinatorInstances.map((instance) => (
                        <Table.Tr key={instance.instanceId}>
                          <Table.Td>
                            <Text size="sm" ff="monospace">{instance.instanceId.slice(0, 8)}...</Text>
                          </Table.Td>
                          <Table.Td>
                            <Group gap="xs">
                              {instance.status === 'HEALTHY' ? <IconCheck size={14} color="green" /> :
                               instance.status === 'WARNING' ? <IconExclamationMark size={14} color="orange" /> :
                               <IconX size={14} color="red" />}
                              <Text size="sm">{instance.status}</Text>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {Math.round((Date.now() - new Date(instance.lastHeartbeat).getTime()) / 1000)}s ago
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{instance.activeSagas}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{(instance.memoryUsageBytes / 1024 / 1024).toFixed(1)}MB</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{instance.cpuUsagePercent.toFixed(1)}%</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{Math.round(instance.uptime / 3600)}h</Text>
                          </Table.Td>
                          <Table.Td>
                            {instance.status !== 'HEALTHY' && (
                              <Button
                                size="xs"
                                color="orange"
                                onClick={() => handleEmergencyIntervention('coordinator', `restart_instance_${instance.instanceId}`)}
                              >
                                Restart
                              </Button>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}

              {selectedComponent === 'locks' && (
                <Grid>
                  <Grid.Col span={6}>
                    <Stack gap="sm">
                      <Text size="sm" fw={600}>Lock Statistics</Text>
                      <Group justify="space-between">
                        <Text size="sm">Active Locks:</Text>
                        <Text size="sm" fw={600}>{health.distributedLocks.activeLocks}</Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm">Average Duration:</Text>
                        <Text size="sm" fw={600}>{health.distributedLocks.averageLockDurationMs}ms</Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm">Expired Locks:</Text>
                        <Text size="sm" fw={600}>{health.distributedLocks.expiredLocks}</Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm">Contention Events:</Text>
                        <Text size="sm" fw={600}>{health.distributedLocks.contentionCount}</Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm">Lock Timeouts:</Text>
                        <Text size="sm" fw={600} c="red">{health.distributedLocks.lockTimeouts}</Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm">Emergency Unlocks:</Text>
                        <Text size="sm" fw={600} c="orange">{health.distributedLocks.emergencyUnlocks}</Text>
                      </Group>
                    </Stack>
                  </Grid.Col>

                  <Grid.Col span={6}>
                    {(health.distributedLocks.lockTimeouts > 0 || health.distributedLocks.emergencyUnlocks > 0) && (
                      <Stack gap="sm">
                        <Text size="sm" fw={600}>Emergency Actions</Text>
                        <Button
                          size="sm"
                          color="orange"
                          leftSection={<IconLockOpen size={14} />}
                          onClick={() => handleEmergencyIntervention('locks', 'emergency_unlock_all')}
                        >
                          Emergency Unlock All
                        </Button>
                        <Button
                          size="sm"
                          color="red"
                          leftSection={<IconShield size={14} />}
                          onClick={() => handleEmergencyIntervention('locks', 'reset_lock_manager')}
                        >
                          Reset Lock Manager
                        </Button>
                      </Stack>
                    )}
                  </Grid.Col>
                </Grid>
              )}
            </Stack>
          </Card>
        )}

        {/* Recent Alerts Timeline */}
        {alerts.length > 0 && (
          <Card withBorder p="lg">
            <Stack gap="md">
              <Group justify="space-between">
                <Title order={4}>Recent Coordination Alerts</Title>
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() => setAlerts([])}
                >
                  Clear All
                </Button>
              </Group>

              <ScrollArea h={400}>
                <Timeline active={-1} lineWidth={2}>
                  {alerts.slice(0, 20).map((alert, index) => (
                    <Timeline.Item
                      key={index}
                      bullet={
                        alert.severity === 'CRITICAL' ? <IconAlertTriangle size={14} /> :
                        alert.severity === 'HIGH' ? <IconExclamationMark size={14} /> :
                        <IconActivity size={14} />
                      }
                      title={
                        <Group gap="sm">
                          <Badge
                            size="sm"
                            color={
                              alert.severity === 'CRITICAL' ? 'red' :
                              alert.severity === 'HIGH' ? 'orange' :
                              alert.severity === 'MEDIUM' ? 'yellow' : 'blue'
                            }
                          >
                            {alert.component}
                          </Badge>
                          <Text size="sm" fw={600}>{alert.alertType}</Text>
                        </Group>
                      }
                    >
                      <Text size="sm" c="dimmed" mb={4}>
                        {new Date(alert.timestamp).toLocaleString()}
                      </Text>
                      <Text size="sm">{alert.message}</Text>
                      {alert.resolution && (
                        <Text size="sm" c="green" mt="xs">
                          Resolved: {alert.resolution}
                        </Text>
                      )}
                    </Timeline.Item>
                  ))}
                </Timeline>
              </ScrollArea>
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
};